import { Dialog } from "@base-ui-components/react/dialog";
import { harnessSupportsChat } from "core";
import { CornerDownLeft, Search } from "lucide-react";
import {
	type KeyboardEvent,
	useCallback,
	useEffect,
	useId,
	useMemo,
	useRef,
	useState,
} from "react";
import { matchPath, useLocation, useNavigate } from "react-router";
import { useAgentsQuery } from "@/lib/api/hooks";
import { cn } from "@/lib/utils";
import {
	buildPaletteEntries,
	type PaletteEntry,
	pushRecent,
	RECENT_LIMIT,
	searchPalette,
} from "./commandPaletteModel";

/* Recent picks are a per-browser convenience: storage can be missing or
 * throw (private windows, blocked site data), and an empty list is fine. */
const RECENTS_KEY = "forgedock.palette-recents";

function readRecents(): string[] {
	try {
		const parsed: unknown = JSON.parse(window.localStorage.getItem(RECENTS_KEY) ?? "[]");
		return Array.isArray(parsed)
			? parsed.filter((id): id is string => typeof id === "string").slice(0, RECENT_LIMIT)
			: [];
	} catch {
		return [];
	}
}

function writeRecents(ids: string[]): void {
	try {
		window.localStorage.setItem(RECENTS_KEY, JSON.stringify(ids));
	} catch {
		// Unavailable storage only costs the recent list.
	}
}

const isMac =
	typeof navigator !== "undefined" &&
	/Mac|iPhone|iPad|iPod/.test(navigator.platform || navigator.userAgent);

const paletteShortcutLabel = isMac ? "⌘K" : "Ctrl K";

/** The sidebar's way in, for anyone who has not learned the shortcut yet. */
export function JumpToButton({ onOpen, className }: { onOpen: () => void; className?: string }) {
	return (
		<button
			type="button"
			onClick={onOpen}
			aria-haspopup="dialog"
			aria-keyshortcuts={isMac ? "Meta+K" : "Control+K"}
			className={cn(
				"flex w-full items-center gap-2 rounded-sm border border-sidebar-border px-2.5 py-1.5 text-left text-[13px] text-sidebar-muted transition-colors duration-150 hover:border-sidebar-foreground/30 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
				className,
			)}
		>
			<Search className="size-3.5 shrink-0" />
			<span className="flex-1">Jump to…</span>
			<kbd className="rounded-sm border border-sidebar-border px-1.5 py-px font-mono text-[10px] text-sidebar-foreground">
				{paletteShortcutLabel}
			</kbd>
		</button>
	);
}

/** Open state for the palette. Opening records what had focus so closing
 * can hand it back: the shortcut has no trigger for the dialog to return to. */
export function useCommandPalette() {
	const [open, setOpen] = useState(false);
	const returnFocusRef = useRef<HTMLElement | null>(null);
	const onOpenChange = useCallback((next: boolean) => {
		if (next && document.activeElement instanceof HTMLElement) {
			returnFocusRef.current = document.activeElement;
		}
		setOpen(next);
	}, []);
	return { open, onOpenChange, returnFocusRef };
}

/**
 * The veterans' fast path: every page, agent, agent tab and setting by name,
 * including the names things had before they moved. Opens on Cmd-K (Ctrl-K
 * off the Mac) from anywhere, and hands focus back where it was on close.
 */
export function CommandPalette({
	open,
	onOpenChange,
	returnFocusRef,
}: ReturnType<typeof useCommandPalette>) {
	useEffect(() => {
		function handleKeyDown(event: globalThis.KeyboardEvent) {
			const modifier = isMac ? event.metaKey : event.ctrlKey;
			if (!modifier || event.altKey || event.shiftKey || event.key.toLowerCase() !== "k") return;
			event.preventDefault();
			onOpenChange(!open);
		}
		window.addEventListener("keydown", handleKeyDown);
		return () => window.removeEventListener("keydown", handleKeyDown);
	}, [open, onOpenChange]);

	return (
		<Dialog.Root open={open} onOpenChange={onOpenChange}>
			<Dialog.Portal>
				<Dialog.Backdrop className="fixed inset-0 z-50 bg-black/60 transition-opacity data-[ending-style]:opacity-0 data-[starting-style]:opacity-0" />
				<Dialog.Popup
					finalFocus={() => {
						const target = returnFocusRef.current;
						returnFocusRef.current = null;
						// The mobile drawer that held the trigger may be gone by now.
						return target?.isConnected ? target : true;
					}}
					className="fixed left-1/2 top-[12vh] z-50 flex max-h-[min(560px,76vh)] w-[calc(100%-2rem)] max-w-xl -translate-x-1/2 flex-col overflow-hidden rounded-md border border-border bg-popover text-popover-foreground shadow-xl transition-all data-[ending-style]:scale-95 data-[ending-style]:opacity-0 data-[starting-style]:scale-95 data-[starting-style]:opacity-0"
				>
					<Dialog.Title className="sr-only">Jump to</Dialog.Title>
					<PaletteSearch onClose={() => onOpenChange(false)} />
				</Dialog.Popup>
			</Dialog.Portal>
		</Dialog.Root>
	);
}

/* Mounted only while the dialog is open, so every open starts fresh: empty
 * query, first option active, recents re-read. */
function PaletteSearch({ onClose }: { onClose: () => void }) {
	const navigate = useNavigate();
	const { pathname } = useLocation();
	const agentsQuery = useAgentsQuery();
	const [query, setQuery] = useState("");
	const [activeIndex, setActiveIndex] = useState(0);
	const [recents] = useState(readRecents);
	const baseId = useId();

	const entries = useMemo(
		() =>
			buildPaletteEntries(
				(agentsQuery.data ?? []).map((agent) => ({
					name: agent.name,
					slug: agent.slug,
					supportsChat: harnessSupportsChat(agent.harness),
				})),
			),
		[agentsQuery.data],
	);
	const currentAgent = matchPath("/agents/:slug/*", pathname)?.params.slug;
	const sections = useMemo(
		() => searchPalette(entries, query, recents, currentAgent),
		[entries, query, recents, currentAgent],
	);
	const options = useMemo(() => sections.flatMap((section) => section.entries), [sections]);
	const active = Math.min(activeIndex, options.length - 1);
	const optionId = (entry: PaletteEntry) => `${baseId}-${entry.id}`;

	const activeId = options[active] ? optionId(options[active]) : undefined;

	useEffect(() => {
		if (activeId) document.getElementById(activeId)?.scrollIntoView({ block: "nearest" });
	}, [activeId]);

	function choose(entry: PaletteEntry) {
		writeRecents(pushRecent(recents, entry.id));
		onClose();
		navigate(entry.to);
	}

	function handleKeyDown(event: KeyboardEvent<HTMLInputElement>) {
		if (options.length === 0) return;
		const move = (index: number) => {
			event.preventDefault();
			setActiveIndex((index + options.length) % options.length);
		};
		if (event.key === "ArrowDown") move(active + 1);
		else if (event.key === "ArrowUp") move(active - 1);
		else if (event.key === "Home" && event.ctrlKey) move(0);
		else if (event.key === "End" && event.ctrlKey) move(options.length - 1);
		else if (event.key === "Enter" && !event.nativeEvent.isComposing) {
			event.preventDefault();
			choose(options[active]);
		}
	}

	const listId = `${baseId}-list`;

	return (
		<>
			<div className="flex items-center gap-2.5 border-b border-border px-4">
				<Search className="size-4 shrink-0 text-muted-foreground" />
				<input
					// biome-ignore lint/a11y/noAutofocus: the dialog exists to be typed into
					autoFocus
					type="text"
					role="combobox"
					aria-expanded={options.length > 0}
					aria-controls={listId}
					aria-activedescendant={activeId}
					aria-autocomplete="list"
					aria-label="Jump to a page, agent or setting"
					placeholder="Jump to a page, agent, tab or setting…"
					autoComplete="off"
					spellCheck={false}
					value={query}
					onChange={(event) => {
						setQuery(event.target.value);
						setActiveIndex(0);
					}}
					onKeyDown={handleKeyDown}
					className="h-12 min-w-0 flex-1 bg-transparent text-sm text-foreground outline-none placeholder:text-muted-foreground"
				/>
				<kbd className="hidden rounded-sm border border-border px-1.5 py-px font-mono text-[10px] text-muted-foreground sm:block">
					Esc
				</kbd>
			</div>

			<div
				id={listId}
				role="listbox"
				aria-label="Destinations"
				className="flex-1 overflow-y-auto p-1.5"
			>
				{options.length === 0 ? (
					<p className="px-3 py-8 text-center font-mono text-xs text-muted-foreground">
						{query.trim() ? `Nothing matches “${query.trim()}”` : "Nothing to jump to yet"}
					</p>
				) : (
					sections.map((section) => {
						const headingId = `${baseId}-${section.group}`;
						return (
							// biome-ignore lint/a11y/useSemanticElements: a listbox groups its options with role="group", a fieldset is not allowed there
							<div key={section.group} role="group" aria-labelledby={headingId} className="pb-1">
								<p
									id={headingId}
									className="label-micro px-2.5 pb-1 pt-2 text-[10px] text-muted-foreground"
								>
									{section.label}
								</p>
								{section.entries.map((entry) => {
									const position = options.indexOf(entry);
									const isActive = position === active;
									return (
										// biome-ignore lint/a11y/useKeyWithClickEvents: the combobox input owns the keyboard (aria-activedescendant); options only take the mouse
										<div
											key={entry.id}
											id={optionId(entry)}
											role="option"
											aria-selected={isActive}
											tabIndex={-1}
											onMouseMove={() => {
												if (!isActive) setActiveIndex(position);
											}}
											// Keep focus in the input so the keyboard keeps working.
											onMouseDown={(event) => event.preventDefault()}
											onClick={() => choose(entry)}
											className={cn(
												"flex cursor-pointer items-center gap-2 rounded-sm px-2.5 py-2 text-[13px] text-foreground/85",
												isActive && "bg-accent text-foreground",
											)}
										>
											{/* A long agent name gives way before the tab it leads to. */}
											<span className="flex min-w-0 flex-1 items-baseline">
												{entry.agent ? (
													<>
														<span className="truncate text-muted-foreground">
															{entry.agent.name}
														</span>
														<span className="shrink-0 px-1.5 text-muted-foreground/60">/</span>
													</>
												) : null}
												<span className={entry.agent ? "shrink-0" : "truncate"}>{entry.label}</span>
											</span>
											<span className="hidden truncate font-mono text-[10px] text-muted-foreground sm:block">
												{entry.to}
											</span>
											<CornerDownLeft
												className={cn(
													"size-3.5 shrink-0 text-muted-foreground",
													!isActive && "invisible",
												)}
											/>
										</div>
									);
								})}
							</div>
						);
					})
				)}
			</div>
		</>
	);
}
